import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Loader from '../../common/loader';
import { getMailingAddress } from '../user-profile/selectors';
import { isEmail, isPhone } from '../../../utils/validators/generalValidators';
import { savePhones } from './edit-utils';
import { ApiError } from '../../common/api-error';
import { getCustomerParty, getPrimaryPhoneInfo, getSecondaryPhoneInfo } from './selectors';
import {
  resetState,
  setEmailErrors,
  setPhoneErrors,
  setLoading,
  setEditingEmailAndPhone,
  setPhoneNumberPrimary,
  setPhoneNumberPrimaryType,
  setPhoneNumberSecondary,
  setPhoneNumberSecondaryType,
  setEmailAddress,
} from '../user-profile/profileReducer';
import {
  AddressHeader,
  ApiErrorSection,
  CancelButton,
  CustomerSubHeader,
  EditButton,
  FullNameContainer,
  FullNameField,
  LoaderMargin,
  MailingAddressContainer,
  MailingAddressLabel,
  NameLabel,
  OnlineProfileHeader,
  OuterProfileContainer,
  ProfileDetailsContainer,
  ProfileMailingAddress,
  SaveCancelContainer,
  SavePhoneAndEmailButton,
} from './styles';
import PhoneDetails from './phone-details';
import EmailDetails from './email-details';
import { getText } from '../../../utils/i18n';

export const CustomerInformation = (props) => {
  const {
    dispatch,
    emailAddress,
    editingEmailAndPhone,
    formErrors,
    i18n,
    isLoading,
    profileMailingAddress,
    phoneNumberPrimary,
    phoneNumberSecondary,
    phoneNumberPrimaryType,
    phoneNumberSecondaryType,
    state,
    customer, //Don't cleanup it's used for 'savePhones'
    customerParty,
  } = props;

  const [email, setEmail] = useState(emailAddress);

  useEffect(() => {
    dispatch(resetState());
    dispatch(setLoading(true));
    props.dispatch(setEmailErrors(null));
    props.dispatch(setPhoneErrors(null));
    if (phoneNumberPrimary === '') {
      dispatch(
        setPhoneNumberPrimary(
          getPrimaryPhoneInfo(state)?.phoneNumber ? getPrimaryPhoneInfo(state)?.phoneNumber : '',
        ),
      );
    }
    if (phoneNumberSecondary === '') {
      dispatch(
        setPhoneNumberSecondary(
          getSecondaryPhoneInfo(state)?.phoneNumber
            ? getSecondaryPhoneInfo(state)?.phoneNumber
            : '',
        ),
      );
    }
    if (phoneNumberPrimaryType.value === '') {
      dispatch(
        setPhoneNumberPrimaryType(
          getPrimaryPhoneInfo(state)?.phoneName
            ? {
                value: getPrimaryPhoneInfo(state)?.phoneName,
                label: getPrimaryPhoneInfo(state)?.phoneName,
              }
            : { value: null, label: 'Select Type' },
        ),
      );
    }
    if (phoneNumberSecondaryType.value === '') {
      dispatch(
        setPhoneNumberSecondaryType(
          getSecondaryPhoneInfo(state)?.phoneName
            ? {
                value: getSecondaryPhoneInfo(state)?.phoneName,
                label: getSecondaryPhoneInfo(state)?.phoneName,
              }
            : { value: null, label: 'Select Type' },
        ),
      );
    }
    dispatch(
      setEmailAddress(
        getCustomerParty(state).emailInfo ? getCustomerParty(state).emailInfo.emailAddr : '',
      ),
    );
    setEmail(getCustomerParty(state).emailInfo ? getCustomerParty(state).emailInfo.emailAddr : '');
    dispatch(setLoading(false));
  }, []);

  const savePhoneAndEmail = async () => {
    if (emailAddress === getCustomerParty(state)?.emailInfo) {
      return dispatch(setEmailErrors('Please enter a valid e-mail address.'));
    }
    dispatch(setEmailAddress(email));
    dispatch(
      savePhones(
        props,
        phoneNumberPrimary, //new Primary
        phoneNumberSecondary, //new Secondary
        phoneNumberPrimaryType.value, //new Primary Type
        phoneNumberSecondary !== '' ? phoneNumberSecondaryType.value : null, //new Secondary Type
        getSecondaryPhoneInfo(state)?.phoneNumber
          ? getSecondaryPhoneInfo(state)?.phoneNumber
          : null, //old SecondaryPhoneNumber
        getSecondaryPhoneInfo(state)?.phoneName, //old SecondaryPhoneType
        getPrimaryPhoneInfo(state)?.phoneNumber ? getPrimaryPhoneInfo(state)?.phoneNumber : null, //oldPrimaryPhoneNumber
        getPrimaryPhoneInfo(state)?.phoneName ? getPrimaryPhoneInfo(state)?.phoneName : null, //oldPrimaryPhoneType
        email,
      ),
    );
    dispatch(setEditingEmailAndPhone(false));
    dispatch(setEmailErrors(null));
    dispatch(setPhoneErrors(null));
  };

  const arePhoneInputsValid = () => {
    let isValid =
      phoneNumberPrimary !== '' &&
      isPhone(phoneNumberPrimary) &&
      phoneNumberPrimaryType.value !== null;
    if (phoneNumberSecondary.trim() !== '') {
      isValid = isValid && isPhone(phoneNumberSecondary) && phoneNumberSecondaryType.value !== null;
    }
    return isValid;
  };
  const isEmailValid = () => {
    return emailAddress !== '' && isEmail(emailAddress);
  };
  const getMailingAddressLineOne = () => {
    const { addr1 } = profileMailingAddress;
    return `${addr1 || ''}`;
  };
  const getMailingAddressLineTwo = () => {
    const { city, stateProvCd, postalCode } = profileMailingAddress;
    return `${city || ''}${city ? ',' : ''} ${stateProvCd || ''} ${postalCode || ''}`;
  };

  return (
    <OuterProfileContainer>
      <OnlineProfileHeader>
        {getText(i18n, 'customerInformation.title', 'Customer Information')}
      </OnlineProfileHeader>
      {isLoading ? (
        <LoaderMargin>
          <Loader />
        </LoaderMargin>
      ) : (
        <>
          <CustomerSubHeader>
            {getText(
              i18n,
              'customerInformation.subHeading',
              'Please fill out all information accurately',
            )}
          </CustomerSubHeader>
          {formErrors && (
            <ApiErrorSection>
              <ApiError
                body={formErrors}
                variant="nomargin"
                titleColor="#d03434"
                title="Form Errors:"
              />
            </ApiErrorSection>
          )}
          <ProfileDetailsContainer>
            <FullNameContainer>
              <NameLabel>Customer Name</NameLabel>
              <FullNameField>{customerParty.nameInfo.commercialName}</FullNameField>
            </FullNameContainer>
            <EmailDetails isDashboard={true} email={email} setEmail={setEmail} />
            <PhoneDetails isDashboard={true} />
            <MailingAddressContainer>
              <AddressHeader>Address</AddressHeader>
              <MailingAddressLabel>Mailing Address</MailingAddressLabel>
              <ProfileMailingAddress>
                {getMailingAddressLineOne()} <br />
                {getMailingAddressLineTwo()}
              </ProfileMailingAddress>
            </MailingAddressContainer>
            {!editingEmailAndPhone && (
              <EditButton
                onClick={() => {
                  dispatch(setEditingEmailAndPhone(true));
                }}
              >
                Edit
              </EditButton>
            )}
            {editingEmailAndPhone && (
              <>
                <CancelButton
                  onClick={() => {
                    dispatch(setEditingEmailAndPhone(false));
                    dispatch(setEmailErrors(null));
                    dispatch(setPhoneErrors(null));
                  }}
                >
                  Cancel
                </CancelButton>
                <SaveCancelContainer>
                  <SavePhoneAndEmailButton
                    onClick={() => {
                      savePhoneAndEmail();
                    }}
                    disabled={!arePhoneInputsValid() || !isEmailValid()}
                  >
                    Apply
                  </SavePhoneAndEmailButton>
                </SaveCancelContainer>
              </>
            )}
          </ProfileDetailsContainer>
        </>
      )}
    </OuterProfileContainer>
  );
};

CustomerInformation.propTypes = {
  dispatch: PropTypes.func,
  i18n: PropTypes.object,
  isLoading: PropTypes.bool,
  customer: PropTypes.object,
  emailAddress: PropTypes.string,
  formErrors: PropTypes.string,
  profileName: PropTypes.string,
  profileMailingAddress: PropTypes.object,
  phoneNumberPrimary: PropTypes.string,
  phoneNumberSecondary: PropTypes.string,
  phoneNumberPrimaryType: PropTypes.object,
  phoneNumberSecondaryType: PropTypes.object,
  phoneErrors: PropTypes.any,
  emailErrors: PropTypes.any,
  editingEmailAndPhone: PropTypes.bool,
  primaryPhoneNameType: PropTypes.string,
  secondaryPhoneNameType: PropTypes.string,
  state: PropTypes.object,
  customerParty: PropTypes.object,
};
const mapStateToProps = (state) => ({
  state,
  i18n: state.i18nSlice.i18n,
  isLoading: state.profileSlice.isLoading,
  systemId: state.customerSlice.customer.systemId,
  customer: state.customerSlice.customer,
  customerParty: getCustomerParty(state),
  emailId: getCustomerParty(state)?.emailInfo?.id,
  formErrors: state.profileSlice.formErrors,
  phoneErrors: state.profileSlice.phoneErrors,
  emailErrors: state.profileSlice.emailErrors,
  editingEmailAndPhone: state.profileSlice.editingEmailAndPhone,
  profileMailingAddress: getMailingAddress(state),
  emailAddress: state.profileSlice.emailAddress,
  oldEmailAddress: getCustomerParty(state)?.emailInfo?.emailAddr,
  phoneNumberPrimary: state.profileSlice.phoneNumberPrimary,
  phoneNumberPrimaryType: state.profileSlice?.phoneNumberPrimaryType,
  phoneNumberSecondaryType: state.profileSlice?.phoneNumberSecondaryType,
  phoneNumberSecondary: state.profileSlice.phoneNumberSecondary,
  phoneNumberPrimaryId: getPrimaryPhoneInfo(state)?.id,
  phoneNumberSecondaryId: getSecondaryPhoneInfo(state)?.id,
});

export default connect(mapStateToProps)(CustomerInformation);
